/* eslint-disable react/jsx-no-bind */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-use';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Button, ModalManager, ThemeProvider } from '@3plearning/react-components';
import PortalCommunication from '../../helpers/portal-comms';
import { getConfiguration, DEFAULT_POLL_DELAY } from '../../helpers/utils';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import {
  studentAssignments,
  updateSortStudents,
  getPublications,
  getGenericCourses,
  getClassrooms,
  updateSelectedCourse,
  updateSelectedAssessmentType
} from './upf-assign-actions';
import css from './upf-assign.scss';
import StudentList from '../../components/student-list/student-list';
import Filter from '../../components/filters/filters';
import Toast from '../../components/toast';
import { getAllLearningJourneys, exportAssignmentsAsCSV } from './upf-assign-util';
import Legend from '../../components/legend';
import { Visible, Info, MoreIcon, ExcelExportIcon } from '../../helpers/icon-dictionary';
import CourseFilters from '../../components/course-filters/course-filters';
import AssessmentFilter from '../../components/assessment-filter/assessment-filter';
import { Standards } from '../../components/standards';
import useInterval from '../../helpers/hooks/use-interval';
import { dismissModal } from '../../containers/modal-manager/modal-manager-actions';
import { ExpandCollapseFooter } from '../expand-collapse-footer/expand-collapse-footer';
import Footer from '../../components/footer';
import ErrorBoundary from '../error-boundary';
import SvgIcon from '../../components/svg-icon/svg-icon';

const queryString = require('query-string');

const POLL_DELAY = getConfiguration('pollDelay') || DEFAULT_POLL_DELAY;

const UpfAssign = ({ i18n }) => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const parsed = queryString.parse(search);

  const selectedProduct = useSelector(state => state.product, shallowEqual);
  const props = useSelector(state => state.UPFAssignReducer);
  const toastProps = useSelector(state => state.toastReducer, shallowEqual);
  const [isLegendVisible, setLegendVisible] = useState(false);
  const publications = useSelector(state => state.UPFAssignReducer.publications);
  const assessmentTypes = useSelector(state => state.UPFAssignReducer.assessmentTypes);
  const selectedAssessmentType = useSelector(state => state.UPFAssignReducer.selectedAssessmentType);

  const classrooms = useSelector(state => state.UPFAssignReducer.classrooms);
  const selectedCourse = useSelector(state => state.UPFAssignReducer.selectedCourse);
  const { selectedTopics } = useSelector(state => state.UPFAssignReducer);
  const previewModal = useSelector(state => state.modalContainerReducer);
  const learningJourneyIds = getAllLearningJourneys(selectedTopics);
  const countryCode = parsed.countryCode;
  const [isPolling, setIsPolling] = useState(false);

  const girlIds = JSON.parse(window._env_.girlIdsForInfoModal || '[]');
  const [showActionsList, setActionsList] = useState(false);
  const { assignmentStatuses, selectedClassRoom } = useSelector(state => state.UPFAssignReducer);

  const assignments = (students) => {
    dispatch(
      studentAssignments({
        productId: selectedProduct && selectedProduct.id,
        courseId: selectedCourse.id,
        courseVersion: selectedCourse.version,
        assessmentTypeId: selectedAssessmentType.id,
        studentUserProfileIds: students.map(student => student.id),
        learningJourneyIds
      })
    );
    dispatch(updateSortStudents(students));
  };

  const getStudentAssignments = () => {
    if (props.filteredStudents.length
      && selectedCourse
      && selectedCourse.id
      && selectedCourse.version
      && selectedAssessmentType
      && selectedAssessmentType.id
    ) {
      assignments(props.filteredStudents);
    }
  };

  const invokeInfoModal = () => {
    PortalCommunication.requestInfoModal(girlIds);
  };

  const genericCourses = (selectCourse) => {
    dispatch(updateSelectedCourse(selectCourse));
  };

  let previewModalTheme = {
    overrides: {
      modal: {
        width: '100%',
        maxWidth: '900px'
      }
    }
  };

  previewModalTheme = Object.assign({}, previewModalTheme);

  // dispatch action to poll result data in very POLL_DELAY secs
  useInterval(() => {
    getStudentAssignments();
  }, isPolling ? POLL_DELAY : null);

  useEffect(() => {
    if (props.pausePolling) {
      setIsPolling(false);
    } else {
      setIsPolling(true);
    }
  }, [props.pausePolling]);

  // get the initial data
  useEffect(() => {
    dispatch(getPublications(i18n));
  }, []);

  // once we have the initial data then go and fetch student assignments
  useEffect(() => {
    getStudentAssignments();
  }, [props.filteredStudents, selectedTopics, selectedAssessmentType]);

  // get the initial data
  useEffect(() => {
    dispatch(getClassrooms(selectedProduct.id));
  }, []);

  useEffect(() => {
    if (!selectedCourse) {
      return;
    }
    const { id, version } = selectedCourse;
    if (id && version) {
      dispatch(getGenericCourses({
        courseId: id,
        courseVersion: version,
        i18n
      }));
    }
  }, [selectedCourse]);

  function hideLegend() {
    setLegendVisible(false);
  }

  const handleAssessmentType = (selectedType) => {
    dispatch(updateSelectedAssessmentType(selectedType));
  };

  const exportAssignments = () => {
    setActionsList(false);
    const assessmentName = selectedTopics[0].assessmentName;
    const selectedClassName = selectedClassRoom.label;
    const assessmentType = selectedAssessmentType.name;
    const exportFileName = `${selectedClassName}_${assessmentName}_${assessmentType}`;
    exportAssignmentsAsCSV({
      selectedTopics,
      assignments: assignmentStatuses,
      students: props.filteredStudents,
      countryCode,
      exportFileName,
      assessmentType,
      i18n
    });
  };

  return (
    <div>
      <div>
        <div className={css.filterBar}>
          <div className={css.courseFilter}>
            <ErrorBoundary>
              <CourseFilters
                countryCode={countryCode}
                publications={publications}
                genericCourses={genericCourses}
                i18n={i18n} />
            </ErrorBoundary>
          </div>

          <div className={css.assessmentFilter}>
            <ErrorBoundary>
              <AssessmentFilter
                types={assessmentTypes}
                onSelectOption={handleAssessmentType}
                selectedOption={selectedAssessmentType}
                i18n={i18n} />
            </ErrorBoundary>
          </div>
          <div className={css.buttonsContainer}>
            {
              girlIds.length === 0 ? <div /> : <Button
                alignIcon="LEFT"
                className={css.howThisWorks}
                onClick={invokeInfoModal}
                icon={Info}
                text={i18n.t`How does this work?`}
                primary
                type={Button.LOZENGE} />
            }
            <div className={css.bottomButtons}>
              <span
                id="legendButton"
                onClick={() => { setLegendVisible(!isLegendVisible); }}
                role="button"
                tabIndex="0">
                <Button
                  alignIcon="LEFT"
                  className={css.key}
                  icon={Visible}
                  onClick={() => { }}
                  primary
                  text={i18n.t`Key`}
                  type={Button.LOZENGE} />
              </span>
              <OutsideClickHandler
                callback={() => setActionsList(false)}
                forButton="actionsButton"
              >
                <span className={css.actions}>
                  <Button
                    alignIcon="LEFT"
                    className={`${css.key} ${css.keyActive} ${
                      showActionsList && css.actionsActive
                    }`}
                    icon={MoreIcon}
                    onClick={() => setActionsList(!showActionsList)}
                    primary
                    text={i18n.t`Actions`}
                    type={Button.LOZENGE}
                  />
                  {showActionsList && (
                    <ul className={css.actionsList}>
                      <li>
                        <div role="button" tabIndex="0" onClick={exportAssignments}>
                          <SvgIcon icon={ExcelExportIcon} />
                          <span className={css.actionsListText}>
                            {i18n.t`Export to Excel`}
                          </span>
                        </div>
                      </li>
                    </ul>
                  )}
                </span>
              </OutsideClickHandler>
            </div>
          </div>
          <OutsideClickHandler callback={hideLegend} forButton="legendButton">
            <ErrorBoundary>
              <Legend
                i18n={i18n}
                isVisible={isLegendVisible} />
            </ErrorBoundary>
          </OutsideClickHandler>
        </div>

        <div className={css.component}>
          <div className={css.leftSection}>
            <ErrorBoundary>
              <Filter
                {...props}
                classrooms={classrooms}
                lastSelectedClass={parsed.classId}
                lastSelectedGroup={parsed.groupId}
                i18n={i18n} />
            </ErrorBoundary>
            <ErrorBoundary>
              <StudentList learningJourneyIds={learningJourneyIds} i18n={i18n} />
            </ErrorBoundary>
          </div>
          <div
            className={css.rightSection}
            onScroll={(e) => {
              if (document.getElementById('studentList')) {
                document.getElementById('studentList').scrollTop = e.target.scrollTop;
              }
            }}>
            <ErrorBoundary>
              <Standards
                i18n={i18n}
                learningJourneyIds={learningJourneyIds} />
            </ErrorBoundary>
          </div>
        </div>
        <div className={css.footer}>
          <ErrorBoundary>
            <Toast {...toastProps} />
          </ErrorBoundary>
        </div>
        <ExpandCollapseFooter>
          <Footer i18n={i18n} />
        </ExpandCollapseFooter>
        <ThemeProvider theme={previewModalTheme}>
          <ErrorBoundary>
            <ModalManager
              modals={previewModal}
              onClose={
                previewModal.length && previewModal[previewModal.length - 1].noCloseButton ? null : () => {
                  dispatch(dismissModal());
                }
              }
            />
          </ErrorBoundary>
        </ThemeProvider>
      </div>
    </div>
  );
};

UpfAssign.propTypes = {
  filteredStudents: PropTypes.array,
  selectedStudentIds: PropTypes.array,
  selectedPSRs: PropTypes.array,
  selectedCells: PropTypes.array,
  i18n: PropTypes.object,
  playLists: PropTypes.array,
  pausePolling: PropTypes.bool,
  isLoading: PropTypes.bool,
  selectedPlaylistId: PropTypes.bool,
  classrooms: PropTypes.array
};

UpfAssign.whyDidYouRender = false;

export default UpfAssign;
